import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Tooltip } from "@mui/material";
import { FS } from "@plannel/utils/font-util";
import { C } from "@plannel/components/ai/aiTokens";
import { toneDark, toneLight, toneMain } from "@plannel/pages/ai/term/termColors";

// 그리드 별칭 셀 — 한 줄에 들어가는 만큼 태그로, 나머지는 +N 과 전체 목록 팝오버
const TAG_PX = 8;
const GAP = 4;
const TAG_SX = {
  display: "inline-block", flexShrink: 0, maxWidth: "100%", boxSizing: "border-box", height: 20, lineHeight: "20px",
  px: `${TAG_PX}px`, borderRadius: "4px", bgcolor: C.bg3, color: C.text1,
  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
};

// 테넌트가 추가한 별칭은 변경 구분 배지와 같은 색(termColors)의 테두리 — 옅은 바탕만으로는 표준 태그·선택 행 배경과 구분이 안 된다
export const addedSx = (tone = "override") => ({
  bgcolor: toneLight(tone), color: toneDark(tone), boxShadow: (th) => `inset 0 0 0 1px ${toneMain(tone)(th)}`, fontWeight: 600,
});
// 제외한 표준 별칭은 모달의 제외 칩과 같은 주황 테두리·취소선
const EXCLUDED_SX = {
  bgcolor: "transparent", color: toneMain("excluded"), textDecoration: "line-through",
  boxShadow: (th) => `inset 0 0 0 1px ${toneMain("excluded")(th)}`,
};
const tagSx = (a, added, tone, excluded) => {
  if (excluded?.has(a.toLowerCase())) return { ...TAG_SX, ...EXCLUDED_SX };
  return added?.has(a.toLowerCase()) ? { ...TAG_SX, ...addedSx(tone) } : TAG_SX;
};

let canvas;
const textWidth = (text, font) => {
  canvas = canvas || document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  ctx.font = font;
  return Math.ceil(ctx.measureText(text).width);
};

// +N 폭을 먼저 떼어 두고 앞에서부터 들어가는 개수를 센다. 최소 1개는 보이고 넘치면 말줄임
export const fitCount = (widths, avail, plusWidth) => {
  const n = widths.length;
  let used = 0;
  for (let k = 0; k < n; k += 1) {
    const rest = n - k - 1;
    const need = used + (k ? GAP : 0) + widths[k] + (rest ? GAP + plusWidth(rest) : 0);
    if (need > avail) return Math.max(k, 1);
    used += (k ? GAP : 0) + widths[k];
  }
  return n;
};

// 표준 툴팁 배경 위 목록 — 태그는 옅은 회색, 추가 별칭은 채운 파랑
const LIST_TAG_SX = { ...TAG_SX, bgcolor: "rgba(0,0,0,0.07)", color: "inherit", fontSize: FS.CAPTION };
const listAddedSx = (tone) => ({ ...LIST_TAG_SX, bgcolor: toneMain(tone), color: "common.white" });

const AliasList = ({ aliases, added, excluded, tone }) => {
  const { t } = useTranslation();
  return (
    <Box sx={{ py: 0.25, maxWidth: 320 }}>
      <Box sx={{ mb: 0.5 }}>{`${t("aiTermAliases")} ${aliases.length}`}</Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: `${GAP}px` }}>
        {aliases.map((a) => (
          <Box key={a} component="span" sx={excluded?.has(a.toLowerCase()) ? { ...LIST_TAG_SX, ...EXCLUDED_SX }
            : added?.has(a.toLowerCase()) ? listAddedSx(tone) : LIST_TAG_SX}>{a}</Box>
        ))}
      </Box>
    </Box>
  );
};

const TermAliasCell = ({ aliases, added, excluded, tone = "override", eGridCell }) => {
  const box = useRef(null);
  const [fit, setFit] = useState({ count: aliases.length, cut: false });
  const [hover, setHover] = useState(false);
  const [keyOpen, setKeyOpen] = useState(false);

  useLayoutEffect(() => {
    const el = box.current;
    if (!el || !aliases.length) return undefined;
    let frame = 0;
    const measure = () => {
      const font = getComputedStyle(el).font;
      const avail = el.clientWidth;
      const widths = aliases.map((a) => textWidth(a, font) + TAG_PX * 2);
      // +N 은 테두리 1px 씩 더 넓다
      const plus = (r) => textWidth(`+${r}`, font) + TAG_PX * 2 + 2;
      const count = fitCount(widths, avail, plus);
      const rest = aliases.length - count;
      setFit({ count, cut: widths[0] > avail - (rest ? GAP + plus(rest) : 0) });
    };
    // 열 폭을 끄는 동안 매 프레임 다시 재지 않도록 한 프레임에 한 번만
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    });
    measure();
    ro.observe(el);
    return () => { ro.disconnect(); cancelAnimationFrame(frame); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aliases.join("")]);

  const hidden = aliases.length - fit.count;

  // 셀 포커스 상태에서 Enter/Space 로 목록을 연다(그리드는 셀 단위로만 포커스가 이동한다)
  useEffect(() => {
    if (!eGridCell || !hidden) return undefined;
    const onKey = (e) => {
      if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setKeyOpen((v) => !v); }
      else if (e.key === "Escape") setKeyOpen(false);
    };
    const onBlur = () => setKeyOpen(false);
    eGridCell.addEventListener("keydown", onKey);
    eGridCell.addEventListener("focusout", onBlur);
    return () => { eGridCell.removeEventListener("keydown", onKey); eGridCell.removeEventListener("focusout", onBlur); };
  }, [eGridCell, hidden]);

  // 빈 값은 다른 열과 같이 공란 — 기호가 세로로 늘어서면 괘선처럼 보인다
  if (!aliases.length) return null;
  return (
    <Box ref={box} sx={{ display: "flex", alignItems: "center", gap: `${GAP}px`, height: "100%", overflow: "hidden", fontSize: FS.CAPTION }}>
      {/* 잘린 첫 태그의 전문은 화면 표준 툴팁(MUI)으로 — 브라우저 기본 title 은 검은 툴팁이라 쓰지 않는다 */}
      {/* 두 툴팁 모두 아래 행을 덮는 위치라 셀을 벗어나면 바로 닫는다 — 안 닫히면 아래 행 셀에 올릴 수 없다 */}
      {aliases.slice(0, fit.count).map((a, i) => (
        <Tooltip key={a} title={fit.cut && i === 0 ? a : ""} placement="bottom-start" disableInteractive>
          <Box component="span" sx={{ ...tagSx(a, added, tone, excluded), minWidth: 0, flexShrink: i === 0 ? 1 : 0 }}>{a}</Box>
        </Tooltip>
      ))}
      {hidden > 0 && (
        <Tooltip title={<AliasList aliases={aliases} added={added} excluded={excluded} tone={tone} />} open={hover || keyOpen} placement="bottom-start" disableInteractive
          onOpen={() => setHover(true)} onClose={() => { setHover(false); setKeyOpen(false); }}>
          <Box component="span" sx={{ ...TAG_SX, flexShrink: 0, bgcolor: "transparent", border: `1px solid ${C.border}`, color: C.text2, cursor: "default" }}>
            {`+${hidden}`}
          </Box>
        </Tooltip>
      )}
    </Box>
  );
};

export default TermAliasCell;
